import type { Locale } from '@/i18n/locales'

/**
 * The currency stored amounts are quoted in. Facts and WAPI hand this layer canonical
 * amounts and a display currency exists only at render
 * (→ invariants.md#facts-canonical-store-editorial); psearch is the one source that
 * converts on request and answers in the currency it was asked for.
 */
export const CANONICAL_CURRENCY = 'USD'

/**
 * The currencies this app is willing to state a price in — every currency any Wanderu
 * surface prices in, so no explicit `?cur=` belongs to another surface.
 *
 * Mirrored from the legacy application's own `supportedCurrencies` list, since nothing in
 * this repo can read it. Deliberately **not** derived from the rate source's published
 * list of quotable codes: that list is advisory in both directions and omits currencies
 * the source does in fact quote, so a set derived from it would refuse a currency this app
 * can price in. A displayable currency the source holds no rate for is a page that states
 * canonical amounts, not a URL that redirects.
 */
export const DISPLAY_CURRENCIES = [
    'CAD',
    'CHF',
    'CZK',
    'DKK',
    'EUR',
    'GBP',
    'HRK',
    'HUF',
    'MXN',
    'NOK',
    'PLN',
    'RON',
    'SEK',
    'TRY',
    'USD',
] as const

export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number]

/**
 * Each served locale's default display currency — the served-locale rows of the shared
 * Wanderu locale→currency mapping, adopted rather than invented. Repointing a
 * locale, or adding one, is a change to a convention other surfaces also apply: a visitor
 * moving between surfaces keeps their currency only because both read the same table.
 *
 * Typed on `Locale` so a new served locale is a compile error until someone decides which
 * currency it prices in, rather than silently falling back.
 */
export const LOCALE_DEFAULT_CURRENCY: Record<Locale, DisplayCurrency> = {
    'en-us': 'USD',
    'es-mx': 'MXN',
    'en-ca': 'CAD',
    'fr-ca': 'CAD',
    'it-it': 'EUR',
    'nl-nl': 'EUR',
    'de-de': 'EUR',
    'en-gb': 'GBP',
}

export function isDisplayCurrency(value: string): value is DisplayCurrency {
    return (DISPLAY_CURRENCIES as readonly string[]).includes(value)
}
